import { useMemo } from "react"

import {
  BUCKET_INTERVAL,
  calculateBucketEndTime,
  type BucketInterval,
} from "@/utils/date/timeBucketing"

import {
  useFlexibleDashboardDrilldownTransportQuery,
  useHumanReviewDashboardDrilldownTransportQuery,
} from "../routes/dashboardTransportQueries"
import type { ChartClickData } from "../widgets/types/chart"
import { GROUP_BY_TYPE } from "../widgets/types/chart-types"
import { DATA_SOURCE, type DrillDownRunRecord } from "../widgets/types/core"
import { getBrowserTimezone } from "../widgets/utils/chart/timezoneUtils"
import { isHumanReviewSource } from "../widgets/utils/dataSource"
import {
  isTransportDashboardQueryLoading,
  normalizeDrilldownRuns,
  serializeFlexibleDashboardDrilldownRequest,
  serializeHumanReviewDashboardDrilldownRequest,
} from "../widgets/utils/transportDashboardQueries"

const EMPTY_FLEXIBLE_DRILLDOWN_REQUEST =
  serializeFlexibleDashboardDrilldownRequest({
    metricId: "",
    startDate: "",
    endDate: "",
    monitoring: false,
    dimensionValue: "",
    timezone: "UTC",
  })

const EMPTY_HUMAN_REVIEW_DRILLDOWN_REQUEST =
  serializeHumanReviewDashboardDrilldownRequest({
    metricId: "",
    startDate: "",
    endDate: "",
  })

interface UseUnifiedMetricDrillDownRowsInput {
  clickData: ChartClickData | null
  enabled?: boolean
}

export interface UseUnifiedMetricDrillDownRowsResult {
  rows: DrillDownRunRecord[]
  isLoading: boolean
  error: unknown
  simulationOutputIds: string[]
  uniqueSimulationOutputIds: string[]
  bucketEndTime: string
  timezone: string
}

export function useUnifiedMetricDrillDownRows({
  clickData,
  enabled = true,
}: UseUnifiedMetricDrillDownRowsInput): UseUnifiedMetricDrillDownRowsResult {
  const timezone = getBrowserTimezone()
  const bucketEndTime = useMemo(() => {
    if (!clickData) {
      return ""
    }

    return (
      clickData.endDate ??
      calculateBucketEndTime(
        clickData.bucketTime,
        (clickData.bucketInterval as BucketInterval) ||
          BUCKET_INTERVAL.FOUR_HOURS,
      )
    )
  }, [clickData])

  const isHumanReview = clickData
    ? isHumanReviewSource(clickData.dataSource)
    : false

  const flexibleRequest = useMemo(() => {
    if (!clickData || isHumanReview) {
      return EMPTY_FLEXIBLE_DRILLDOWN_REQUEST
    }

    return serializeFlexibleDashboardDrilldownRequest({
      metricId: clickData.metricId,
      startDate: clickData.bucketTime,
      endDate: bucketEndTime,
      monitoring: clickData.dataSource === DATA_SOURCE.UPLOADED,
      stringValue: clickData.stringValue,
      filters: clickData.filters,
      groupBy:
        clickData.splitBy === GROUP_BY_TYPE.ASSIGNEE
          ? undefined
          : clickData.splitBy,
      groupByMetadataKey: clickData.groupByMetadataKey,
      metadataGroupKind: clickData.metadataGroupKind,
      metadataTopValues: clickData.metadataTopValues,
      dimensionValue: clickData.splitValue || "",
      timezone,
      metricFilter: clickData.metricFilter,
    })
  }, [bucketEndTime, clickData, isHumanReview, timezone])

  const humanReviewRequest = useMemo(() => {
    if (!clickData || !isHumanReview) {
      return EMPTY_HUMAN_REVIEW_DRILLDOWN_REQUEST
    }

    return serializeHumanReviewDashboardDrilldownRequest({
      metricId: clickData.metricId,
      startDate: clickData.bucketTime,
      endDate: bucketEndTime,
      stringValue: clickData.stringValue,
      filters: clickData.filters,
      groupBy:
        clickData.splitBy === GROUP_BY_TYPE.ASSIGNEE
          ? clickData.splitBy
          : undefined,
      dimensionValue: clickData.splitValue,
      metricFilter: clickData.metricFilter,
    })
  }, [bucketEndTime, clickData, isHumanReview])

  const flexibleQueryEnabled =
    enabled &&
    !isHumanReview &&
    !!clickData?.metricId &&
    !!clickData?.bucketTime
  const flexibleResult = useFlexibleDashboardDrilldownTransportQuery({
    request: flexibleRequest,
    options: {
      enabled: flexibleQueryEnabled,
    },
  })

  const humanReviewQueryEnabled =
    enabled && isHumanReview && !!clickData?.metricId && !!clickData?.bucketTime
  const humanReviewResult = useHumanReviewDashboardDrilldownTransportQuery({
    request: humanReviewRequest,
    options: {
      enabled: humanReviewQueryEnabled,
    },
  })

  const rows = useMemo(
    () =>
      isHumanReview
        ? normalizeDrilldownRuns(humanReviewResult.data?.runs)
        : normalizeDrilldownRuns(flexibleResult.data?.runs),
    [flexibleResult.data?.runs, humanReviewResult.data?.runs, isHumanReview],
  )

  const isLoading = isHumanReview
    ? isTransportDashboardQueryLoading(
        humanReviewResult,
        humanReviewQueryEnabled,
      )
    : isTransportDashboardQueryLoading(flexibleResult, flexibleQueryEnabled)
  const error = isHumanReview ? humanReviewResult.error : flexibleResult.error

  const simulationOutputIds = useMemo(
    () => rows.map((run) => run.simulation_output_id),
    [rows],
  )
  const uniqueSimulationOutputIds = useMemo(
    () => Array.from(new Set(simulationOutputIds)),
    [simulationOutputIds],
  )

  return {
    rows,
    isLoading,
    error,
    simulationOutputIds,
    uniqueSimulationOutputIds,
    bucketEndTime,
    timezone,
  }
}
